import { useState, useEffect } from 'react';
import { staffAPI } from '../../api';
import api from '../../api';


function exportToCSV(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(row => headers.map(h => {
    const val = row[h] === null || row[h] === undefined ? '' : String(row[h]);
    return val.includes(',') || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val;
  }).join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function PayrollPage({ guild }) {
  const [staff, setStaff] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [payingAll, setPayingAll] = useState(false);
  const [payingDept, setPayingDept] = useState(false);
  const [selectedDept, setSelectedDept] = useState('');
  const [departments, setDepartments] = useState([]);
  const [tab, setTab] = useState('outstanding');
  const sym = guild?.currency_symbol || '$';

  const loadData = async () => {
    try {
      const [staffRes, historyRes, deptsRes] = await Promise.all([
        staffAPI.getAll(guild.id),
        api.get(`/api/logs/${guild.id}?limit=50`),
        api.get(`/api/departments/${guild.id}`),
      ]);
      setStaff(staffRes.data);
      setDepartments(deptsRes.data);
      setHistory(historyRes.data.filter(l => l.action === 'pay_staff' || l.action === 'pay_all_staff'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [guild.id]);

  const activeStaff = staff.filter(s => s.status === 'active');
  const staffWithBalance = activeStaff.filter(s => parseFloat(s.current_balance) > 0);
  const totalOwed = staffWithBalance.reduce((sum, s) => sum + parseFloat(s.current_balance || 0), 0);
  const totalPaid = activeStaff.reduce((sum, s) => sum + parseFloat(s.total_paid_out || 0), 0);

  const handlePayOne = async (staffMember) => {
    setPaying(staffMember.discord_id);
    try {
      await api.post(`/api/payroll/${guild.id}/pay-one`, {
        discord_id: staffMember.discord_id,
        amount: parseFloat(staffMember.current_balance)
      });
      await loadData();
    } catch { alert('Payment failed. Please try again.'); }
    finally { setPaying(null); }
  };

  const handlePayAll = async () => {
    if (!window.confirm(`Pay all ${staffWithBalance.length} staff members a total of ${sym}${totalOwed.toFixed(2)}?`)) return;
    setPayingAll(true);
    try {
      await api.post(`/api/payroll/${guild.id}/pay-all`);
      await loadData();
    } catch { alert('Payment failed. Please try again.'); }
    finally { setPayingAll(false); }
  };

  const handlePayDept = async () => {
    if (!selectedDept) return;
    const dept = departments.find(d => d.id === parseInt(selectedDept));
    if (!window.confirm(`Pay all staff in ${dept?.name || 'this department'}?`)) return;
    setPayingDept(true);
    try {
      const res = await api.post(`/api/departments/${guild.id}/${selectedDept}/pay`);
      alert(`✅ Paid ${res.data.count} staff member${res.data.count !== 1 ? 's' : ''} in ${res.data.department} — ${sym}${parseFloat(res.data.totalPaid).toFixed(2)} total.`);
      await loadData();
    } catch (err) {
      alert(err?.response?.data?.error || 'Payment failed.');
    } finally { setPayingDept(false); }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>Loading payroll...
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Payroll</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>Track and manage staff payments</p>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
        {[
          { icon: '⚠️', label: 'Outstanding Balance', value: `${sym}${totalOwed.toFixed(2)}`, sub: `${staffWithBalance.length} staff owed`, color: totalOwed > 0 ? 'var(--warning)' : 'var(--success)' },
          { icon: '✅', label: 'Lifetime Paid Out', value: `${sym}${totalPaid.toFixed(2)}`, sub: 'all time total' },
          { icon: '👥', label: 'Staff to Pay', value: staffWithBalance.length, sub: 'with outstanding balance' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: '14px', padding: '20px', boxShadow: 'var(--shadow-card)',
          }}>
            <div style={{ fontSize: '26px', marginBottom: '8px' }}>{s.icon}</div>
            <div style={{ fontSize: '24px', fontWeight: 800, color: s.color || 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{s.value}</div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '4px' }}>{s.label}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{s.sub}</div>
          </div>
        ))}

        {/* Pay All button card */}
        {staffWithBalance.length > 0 && (
          <div style={{
            background: 'var(--gradient-accent)', borderRadius: '14px', padding: '20px',
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          }}>
            <div style={{ fontSize: '26px', marginBottom: '8px' }}>💵</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#fff', fontFamily: 'var(--font-display)', marginBottom: '4px' }}>
              Pay All Staff
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '12px' }}>
              {sym}{totalOwed.toFixed(2)} total
            </div>
            <button onClick={handlePayAll} disabled={payingAll} style={{
              background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: '8px', padding: '8px', color: '#fff',
              fontFamily: 'var(--font-display)', fontSize: '13px', fontWeight: 700,
              cursor: payingAll ? 'not-allowed' : 'pointer',
            }}>
              {payingAll ? 'Processing...' : '✓ Mark All Paid'}
            </button>
          </div>
        )}

        {/* Pay by Department card */}
        {departments.length > 0 && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px', boxShadow: 'var(--shadow-card)' }}>
            <div style={{ fontSize: '26px', marginBottom: '8px' }}>🏢</div>
            <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', marginBottom: '12px' }}>Pay by Department</div>
            <select value={selectedDept} onChange={e => setSelectedDept(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '2px solid var(--border)', borderRadius: '8px', fontSize: '13px', fontFamily: 'var(--font-body)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', outline: 'none', marginBottom: '10px' }}>
              <option value="">Select department...</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <button onClick={handlePayDept} disabled={!selectedDept || payingDept} style={{
              width: '100%', padding: '8px', background: selectedDept ? 'var(--gradient-accent)' : 'var(--bg-secondary)',
              color: selectedDept ? '#fff' : 'var(--text-muted)', border: 'none', borderRadius: '8px',
              fontSize: '13px', fontWeight: 700, cursor: !selectedDept || payingDept ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-display)',
            }}>
              {payingDept ? 'Processing...' : '✓ Pay Department'}
            </button>
          </div>
        )}
      </div>

      {/* Tabs + CSV export */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-secondary)', borderRadius: '12px', padding: '4px', width: 'fit-content' }}>
          {[
            { key: 'outstanding', label: `Outstanding (${staffWithBalance.length})` },
            { key: 'all', label: `All Staff (${activeStaff.length})` },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '8px 18px', borderRadius: '8px', cursor: 'pointer',
              border: 'none', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600,
              background: tab === t.key ? 'var(--bg-card)' : 'transparent',
              color: tab === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
              boxShadow: tab === t.key ? 'var(--shadow-card)' : 'none',
              transition: 'all 0.15s',
            }}>{t.label}</button>
          ))}
        </div>
        <button onClick={() => exportToCSV(
          staff.map(s => ({
            name: s.display_name || s.username,
            discord_id: s.discord_id,
            rank: s.rank_name || '',
            period_hours: parseFloat(s.period_hours || 0).toFixed(2),
            balance_owed: parseFloat(s.current_balance || 0).toFixed(2),
            total_paid_out: parseFloat(s.total_paid_out || 0).toFixed(2),
            lifetime_earnings: parseFloat(s.lifetime_earnings || 0).toFixed(2),
            status: s.status,
          })),
          `payroll-${guild.name}-${new Date().toISOString().split('T')[0]}.csv`
        )} style={{
          padding: '10px 18px', background: 'var(--bg-card)',
          color: 'var(--text-secondary)', border: '1px solid var(--border)',
          borderRadius: '10px', fontSize: '13px', fontWeight: 600,
          cursor: 'pointer', fontFamily: 'var(--font-body)',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          📥 Export CSV
        </button>
      </div>

      {/* Staff payment table */}
      <div className="table-scroll"><div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 120px',
          padding: '12px 20px', borderBottom: '1px solid var(--border)',
          fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.5px', color: 'var(--text-muted)',
        }}>
          <span>Staff Member</span><span>Rank</span><span>Period Hours</span><span>Balance Owed</span><span>Action</span>
        </div>

        {(tab === 'outstanding' ? staffWithBalance : activeStaff).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            {tab === 'outstanding' ? '🎉 All staff have been paid!' : 'No active staff.'}
          </div>
        ) : (tab === 'outstanding' ? staffWithBalance : activeStaff).map(s => (
          <div key={s.id} style={{
            display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 120px',
            padding: '14px 20px', borderBottom: '1px solid var(--border)',
            transition: 'background 0.15s', alignItems: 'center',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                background: 'var(--gradient-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '13px', fontWeight: 700, color: '#fff',
              }}>{(s.display_name || s.username || '?')[0].toUpperCase()}</div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{s.display_name || s.username}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Total paid: {sym}{parseFloat(s.total_paid_out || 0).toFixed(2)}</div>
              </div>
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{s.rank_name || '—'}</div>
            <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{parseFloat(s.period_hours || 0).toFixed(1)}h</div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: parseFloat(s.current_balance) > 0 ? 'var(--warning)' : 'var(--text-muted)' }}>
              {sym}{parseFloat(s.current_balance || 0).toFixed(2)}
            </div>
            <div>
              {parseFloat(s.current_balance) > 0 ? (
                <button onClick={() => handlePayOne(s)} disabled={paying === s.discord_id} style={{
                  padding: '7px 14px', borderRadius: '8px',
                  background: 'var(--gradient-accent)', color: '#fff',
                  border: 'none', cursor: paying === s.discord_id ? 'not-allowed' : 'pointer',
                  fontSize: '12px', fontWeight: 700, fontFamily: 'var(--font-display)',
                  opacity: paying === s.discord_id ? 0.7 : 1,
                }}>
                  {paying === s.discord_id ? '...' : 'Pay'}
                </button>
              ) : (
                <span style={{ fontSize: '12px', color: 'var(--success)', fontWeight: 600 }}>✓ Paid</span>
              )}
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
